import React, { useState } from "react";
import { Button, Segment } from "semantic-ui-react";
import Editor from "./MembershipAliasEditor";
import useDispatchUtilities from "../../../utilities/useDispatchUtilities";

const MembershipAliases = ({ entity, dispatch }) => {
    const [showEditor, setShowEditor] = useState(false);
    const [editing, setEditing] = useState(null);
    const { setNotification } = useDispatchUtilities(dispatch);

    const durations = {
        30: "One month",
        90: "Three months",
        180: "Six months",
        365: "One year",
    };

    const getErrorMessage = (error, fallback) => {
        const errors = error?.response?.data?.errors;

        if (errors?.useForLinkSignup?.[0]) {
            return errors.useForLinkSignup[0];
        }

        if (errors && typeof errors === "object") {
            const firstError = Object.values(errors)?.[0];

            if (Array.isArray(firstError)) {
                return firstError[0];
            }

            if (typeof firstError === "string") {
                return firstError;
            }

            return fallback;
        }

        return (
            error?.response?.data?.message ||
            error?.response?.data?.errors ||
            fallback
        );
    };

    const onCreate = async (payload) => {
        try {
            const response = await entity.storeMembershipAlias(payload);
            entity.membershipAliases.push(response);
            setNotification("success", "Membership alias created");
            setShowEditor(false);
        } catch (error) {
            setNotification(
                "error",
                getErrorMessage(error, "Failed to create membership alias"),
            );
        }
    };

    const onUpdate = async (payload) => {
        try {
            const response = await entity.updateMembershipAlias(
                editing._id,
                payload,
            );
            const replaceAtIndex = entity.membershipAliases.findIndex(
                ({ _id }) => _id === editing._id,
            );
            entity.membershipAliases[replaceAtIndex] = response;
            setNotification("success", "Membership alias saved");
            setShowEditor(false);
            setEditing(null);
        } catch (error) {
            setNotification(
                "error",
                getErrorMessage(error, "Failed to save membership alias"),
            );
        }
    };

    const onDelete = async (index) => {
        const confirmed = window.confirm(
            "Are you sure you want to delete this membership alias?",
        );
        if (confirmed === true) {
            const id = entity.membershipAliases[index]._id;
            try {
                await entity.deleteMembershipAlias(id);
                entity.membershipAliases.splice(index, 1);
                setNotification("success", "Membership alias deleted");
                setShowEditor(false);
            } catch (_) {
                setNotification("error", "Failed to delete membership alias");
            }
        }
    };

    const onCancel = () => {
        setShowEditor(false);
        setEditing(null);
    };

    const ListAliases = () => {
        if (entity.membershipAliases.length === 0) {
            return (
                <p>This entity currently has no membership aliases enabled</p>
            );
        }
        return entity.membershipAliases.map((alias, index) => {
            return (
                <Segment vertical>
                    <h3>{alias.name}</h3>
                    <p>
                        <b>key:</b> <i>{alias.key}</i>
                    </p>
                    <p>
                        <b>Role:</b> {alias.role.name}
                    </p>
                    <p>
                        <b>Duration:</b> {durations[alias.duration]}
                    </p>
                    <p>
                        <b>Cost:</b> £{alias.cost}
                    </p>
                    <p>
                        <b>Event credit:</b> £{alias.eventCredit}
                    </p>
                    <p>
                        <b>Green Fee Credit:</b> £{alias.teeTimeCredit}
                    </p>
                    <p>
                        <b>Renewal exempt:</b>{" "}
                        {alias.renewalExempt ?? true ? "Yes" : "No"}
                    </p>
                    <p>
                        <b>Use for membership link signup:</b>{" "}
                        {alias.useForLinkSignup ? "Yes" : "No"}
                    </p>

                    <p>
                        <b>Default commencement month:</b>{" "}
                        {alias.defaultCommencementMonth
                            ? new Date(
                                  2000,
                                  alias.defaultCommencementMonth - 1,
                                  1,
                              ).toLocaleString("default", { month: "long" })
                            : "Current month"}
                    </p>
                    <Button
                        primary
                        onClick={() => {
                            setEditing(alias);
                            setShowEditor(true);
                        }}
                        className='theme mt-3'
                    >
                        Edit
                    </Button>
                    <Button
                        primary
                        onClick={() => onDelete(index)}
                        className='red mt-3'
                    >
                        Delete
                    </Button>
                </Segment>
            );
        });
    };

    return (
        <div className={"mt-3"}>
            <p>
                Create membership aliases for Mashie roles to use for members
                belonging to this entity
            </p>
            {showEditor ? (
                editing === null ? (
                    <Editor onSave={onCreate} onClose={onCancel} />
                ) : (
                    <Editor
                        onSave={onUpdate}
                        onClose={onCancel}
                        editing={editing}
                    />
                )
            ) : (
                <Button
                    primary
                    onClick={() => setShowEditor(true)}
                    className='theme mt-3'
                >
                    Create alias
                </Button>
            )}
            <h2>Current aliases</h2>
            <ListAliases />
        </div>
    );
};

export default MembershipAliases;
